import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, CheckCircle2, XCircle, Banknote, Trash2, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-2xl font-display font-extrabold font-mono mt-2 ${accent || "text-slate-900"}`}>{value}</p>
    </div>
  );
}

export default function PayrollDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [run, setRun] = useState(null);

  const load = () => api.get(`/payroll/${id}`).then((r) => setRun(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submit = () => act(() => api.post(`/payroll/${id}/submit`), "Submitted for verification");
  const approve = () => act(() => api.post(`/payroll/${id}/verify`, { decision: "approved", note: "" }), "Payroll approved");
  const reject = () => {
    const note = window.prompt("Reason for rejection?") || "";
    act(() => api.post(`/payroll/${id}/verify`, { decision: "rejected", note }), "Payroll rejected");
  };
  const process = () => act(() => api.post(`/payroll/${id}/process`), "Payroll processed & disbursed");
  const remove = () => {
    if (!window.confirm("Delete this draft?")) return;
    act(async () => { await api.delete(`/payroll/${id}`); navigate("/app/payroll"); }, "Deleted");
  };

  if (!run) return <div className="text-slate-400">Loading…</div>;

  const isManager = user?.role === "manager" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const downloadPaystub = () => window.open(`${BACKEND_URL}/api/payroll/${id}/paystub`, "_blank");

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/app/payroll")} data-testid="back-to-payroll" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back to payroll</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-extrabold text-slate-900">{run.company_name}</h1>
            <StatusBadge status={run.status} />
          </div>
          <p className="text-slate-500 mt-1">Week of {new Date(run.week_start).toLocaleDateString()} — {new Date(run.week_end).toLocaleDateString()} · Tax estimate {Math.round(run.tax_rate * 100)}%</p>
          {run.verify_note && run.status === "rejected" && <p className="text-rose-600 text-sm mt-2 font-medium">Rejection note: {run.verify_note}</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          {(run.status === "processed" || run.status === "approved") && (
            <Button onClick={downloadPaystub} variant="outline" data-testid="download-paystub-button" className="rounded-full border-slate-300 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Download paystub</Button>
          )}
          {isManager && (run.status === "draft" || run.status === "rejected") && (
            <>
              <Button onClick={submit} data-testid="submit-payroll-approval-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><Send className="w-4 h-4 mr-1.5" /> Submit for approval</Button>
              <Button onClick={remove} variant="outline" data-testid="delete-payroll-button" className="rounded-full border-slate-300 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></Button>
            </>
          )}
          {isAdmin && run.status === "pending_approval" && (
            <>
              <Button onClick={approve} data-testid="admin-verify-payroll-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-full font-semibold"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Verify & approve</Button>
              <Button onClick={reject} data-testid="admin-reject-payroll-button" variant="outline" className="rounded-full border-slate-300 text-rose-600 hover:bg-rose-50"><XCircle className="w-4 h-4 mr-1.5" /> Reject</Button>
            </>
          )}
          {isAdmin && run.status === "approved" && (
            <Button onClick={process} data-testid="process-payroll-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><Banknote className="w-4 h-4 mr-1.5" /> Process & disburse</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <Stat label="Gross wages" value={money(run.totals.gross)} />
        <Stat label="Tax estimate" value={money(run.totals.tax)} accent="text-amber-600" />
        <Stat label="Net payout" value={money(run.totals.net)} accent="text-sky-600" />
        <Stat label="Total hours" value={`${run.totals.total_hours}h`} />
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="payroll-breakdown-table">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-lg">Employee breakdown</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Employee</th>
              <th className="text-right font-semibold px-6 py-3">Reg hrs</th>
              <th className="text-right font-semibold px-6 py-3">OT hrs</th>
              <th className="text-right font-semibold px-6 py-3">Rate</th>
              <th className="text-right font-semibold px-6 py-3">Gross</th>
              <th className="text-right font-semibold px-6 py-3">Tax</th>
              <th className="text-right font-semibold px-6 py-3">Net</th>
              <th className="text-left font-semibold px-6 py-3">Method</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {run.items.map((it) => (
              <tr key={it.employee_id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2.5">
                    {it.avatar_url && <img src={it.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />}
                    <div><p className="font-semibold text-slate-800">{it.name}</p><p className="text-xs text-slate-500">{it.position || "—"}</p></div>
                  </div>
                </td>
                <td className="px-6 py-3 text-right font-mono text-slate-700">{it.regular_hours}</td>
                <td className="px-6 py-3 text-right font-mono text-amber-600">{it.overtime_hours}</td>
                <td className="px-6 py-3 text-right font-mono text-slate-600">{money(it.hourly_rate)}</td>
                <td className="px-6 py-3 text-right font-mono text-slate-700">{money(it.gross)}</td>
                <td className="px-6 py-3 text-right font-mono text-slate-500">{money(it.tax)}</td>
                <td className="px-6 py-3 text-right font-mono font-bold text-sky-600">{money(it.net)}</td>
                <td className="px-6 py-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{it.payment_method === "check" ? "Check" : "Deposit"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
